/**
 * Review-Pipeline, Frontend-Spiegel (Spec 2026-09-05, E8).
 *
 * ⚠ SYNC REQUIRED: die Regel lebt in supabase/functions/_shared/review-platform.ts.
 * Deno und Vite koennen kein Modul teilen; reviewPlatform.test.ts liest die
 * Edge-Datei per fs und faengt jede Drift bei URLs, Laenderliste und Schwelle.
 * Die Karte muss dieselbe Plattform zeigen, die der Sweep waehlt, sonst
 * bekommt ein Nutzer zwei verschiedene Formulare.
 */

export type ReviewPlatform = 'omr' | 'g2';
export type ReviewMedium = 'email' | 'app';

export const REVIEW_PLATFORMS: readonly ReviewPlatform[] = ['omr', 'g2'];

/** D3: diese Laender gehen zu OMR Reviews, alle anderen zu G2. */
export const OMR_COUNTRY_CODES: readonly string[] = ['DE', 'AT', 'CH'];

/** D4: Wertmoment = erster manueller completed-Lauf mit mindestens so vielen Produkten.
 *  Dieselbe Zahl steht in der View review_candidates (Migration 20260905140000, `>= 20`);
 *  der SQL-Textwaechter src/test/review-pipeline-sql.test.ts vergleicht beide. */
export const REVIEW_MIN_COMPLETED_PRODUCTS = 20;

export const REVIEW_PROFILE_URLS: Record<ReviewPlatform, string> = {
  omr: 'https://omr.com/de/reviews/product/feedoptimizer-ai',
  g2: 'https://www.g2.com/products/feedoptimizer-ai/reviews',
};

export const REVIEW_WRITE_URLS = {
  omr_de: 'https://omr.com/de/reviews/new/product/feedoptimizer-ai',
  omr_en: 'https://omr.com/en/reviews/new/product/feedoptimizer-ai',
  g2: 'https://www.g2.com/products/feedoptimizer-ai/reviews/start',
} as const;

function isGermanLanguage(language: string | null | undefined): boolean {
  return (language ?? '').trim().toLowerCase().startsWith('de');
}

/**
 * D3: Land zuerst (DE/AT/CH -> omr, sonst g2). Ohne Land entscheidet die Sprache
 * (de -> omr, sonst g2). Live sind alle 20 country_code NULL (05.09.2026), der
 * Sprach-Fallback ist damit der Regelfall.
 */
export function resolveReviewPlatform(
  countryCode: string | null | undefined,
  language: string | null | undefined,
): ReviewPlatform {
  const cc = (countryCode ?? '').trim().toUpperCase();
  if (cc !== '') return OMR_COUNTRY_CODES.includes(cc) ? 'omr' : 'g2';
  return isGermanLanguage(language) ? 'omr' : 'g2';
}

/**
 * Deep-Link direkt ins Bewertungsformular (nicht auf die Profilseite), mit UTM.
 * Klick-Messung kommt aus GA4 (`cta_click`, D6), nicht aus einem Redirect.
 */
export function reviewWriteUrl(
  platform: ReviewPlatform,
  language: string | null | undefined,
  medium: ReviewMedium,
): string {
  const base = platform === 'g2'
    ? REVIEW_WRITE_URLS.g2
    : (isGermanLanguage(language) ? REVIEW_WRITE_URLS.omr_de : REVIEW_WRITE_URLS.omr_en);
  const params = new URLSearchParams({
    utm_source: 'feedoptimizer',
    utm_medium: medium,
    utm_campaign: `review_${platform}`,
  });
  return `${base}?${params.toString()}`;
}
